import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { apiClient } from '../config/api';
import API_CONFIG from '../config/api';

/**
 * Custom hook for managing tag/mention notifications
 * These are notifications when a user is tagged (@client) in an incident or customer request chat
 */
export const useTagNotifications = (options = {}) => {
  const { limit = 10 } = options;
  const { getToken } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  /**
   * Fetch tag notifications from the API
   */
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await apiClient.get(API_CONFIG.ENDPOINTS.TAG_NOTIFICATIONS, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { limit }
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (error) {
      console.error("Failed to fetch tag notifications:", error);
      // Set empty state on error
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [getToken, limit]);

  /**
   * Mark a single notification as read
   */
  const markAsRead = async (notificationId) => {
    try {
      const token = await getToken();
      await apiClient.post(
        API_CONFIG.ENDPOINTS.TAG_NOTIFICATION_READ(notificationId),
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark tag notification as read:", error);
    }
  };

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      await apiClient.post(
        API_CONFIG.ENDPOINTS.TAG_NOTIFICATIONS_READ_ALL,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all tag notifications as read:", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Send a tag notification when a user mentions a client in chat
   * @param {Object} params - Tag notification parameters
   * @param {string} params.targetClientId - The client ID being tagged
   * @param {string} params.targetClientName - The client name being tagged
   * @param {string} params.sourceType - "incident" or "customer_request"
   * @param {string} params.sourceId - The incident ID or customer request ID
   * @param {string} params.sourceTitle - The title of the incident or request
   * @param {string} params.message - The chat message containing the tag
   */
  const sendTagNotification = async (params) => {
    try {
      const token = await getToken();
      await apiClient.post(
        API_CONFIG.ENDPOINTS.SEND_TAG_NOTIFICATION,
        {
          targetClientId: params.targetClientId,
          targetClientName: params.targetClientName,
          sourceType: params.sourceType,
          sourceId: params.sourceId,
          sourceTitle: params.sourceTitle,
          message: params.message,
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      return true;
    } catch (error) {
      console.error("Failed to send tag notification:", error);
      return false;
    }
  };

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    sendTagNotification,
    refetch: fetchNotifications
  };
};

export default useTagNotifications;

